import React, { useState, useMemo } from "react";
import GameTableRow from "./GameTableRow";
import { FaSort } from "react-icons/fa6";
import "./GamesTable.scss";

const GamesTable = ({
  games,
  onCreateGame,
  onEditGame,
  onDuplicateGame,
  onToggleActive,
  onDeleteGame,
}) => {
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  const sortedGames = useMemo(() => {
    const sorted = [...games].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "notes":
          aValue = (a.adminNotes || "").toLowerCase();
          bValue = (b.adminNotes || "").toLowerCase();
          break;
        case "route":
          aValue = a.id;
          bValue = b.id;
          break;
        case "type":
          aValue = (a.type || "connections").toLowerCase();
          bValue = (b.type || "connections").toLowerCase();
          break;
        case "status":
          const now = new Date();
          const aBeforeStart = a.activeFrom && new Date(a.activeFrom) > now;
          const aAfterEnd = a.activeUntil && new Date(a.activeUntil) < now;
          const bBeforeStart = b.activeFrom && new Date(b.activeFrom) > now;
          const bAfterEnd = b.activeUntil && new Date(b.activeUntil) < now;
          const aStatus = !a.isActive
            ? "inactive"
            : aBeforeStart
            ? "scheduled"
            : aAfterEnd
            ? "expired"
            : "active";
          const bStatus = !b.isActive
            ? "inactive"
            : bBeforeStart
            ? "scheduled"
            : bAfterEnd
            ? "expired"
            : "active";
          aValue = aStatus;
          bValue = bStatus;
          break;
        case "createdAt":
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [games, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  return (
    <div className="games-section">
      <div className="section-header">
        <span>Games ({games.length})</span>
        <button className="btn btn-primary" onClick={onCreateGame}>
          Create New Game
        </button>
      </div>
      {games.length === 0 ? (
        <div className="empty-state">
          <p>No games created yet. Click "Create New Game" to get started.</p>
        </div>
      ) : (
        <div className="games-table">
          <table>
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort("name")}>
                  <span className="header-content">
                    Display Name
                    <FaSort className="sort-icon" />
                  </span>
                </th>
                <th className="sortable" onClick={() => handleSort("route")}>
                  <span className="header-content">
                    Game Link
                    <FaSort className="sort-icon" />
                  </span>
                </th>
                <th className="sortable" onClick={() => handleSort("type")}>
                  <span className="header-content">
                    Type
                    <FaSort className="sort-icon" />
                  </span>
                </th>
                <th className="sortable" onClick={() => handleSort("status")}>
                  <span className="header-content">
                    Status
                    <FaSort className="sort-icon" />
                  </span>
                </th>
                <th className="sortable" onClick={() => handleSort("notes")}>
                  <span className="header-content">
                    Notes
                    <FaSort className="sort-icon" />
                  </span>
                </th>
                <th
                  className="sortable"
                  onClick={() => handleSort("createdAt")}
                >
                  <span className="header-content">
                    Created
                    <FaSort className="sort-icon" />
                  </span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedGames.map((game) => (
                <GameTableRow
                  key={game.id}
                  game={game}
                  onEdit={onEditGame}
                  onDuplicate={onDuplicateGame}
                  onToggleActive={onToggleActive}
                  onDelete={onDeleteGame}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GamesTable;
